import { formatDate } from '@/ui/formatters/formatDate';
import type {
  CodeDirectoryDto,
  CodeFileDto,
  CoverageFileDto,
  CoverageRunDto,
} from '@flowqa/shared';

type FilterOption = {
  label: string;
  value: string;
};

const buildCoverageRunOptionLabel = (run: CoverageRunDto): string => {
  const capturedAt = run.capturedAt ?? run.createdAt;
  const timestamp = formatDate(capturedAt, { includeTime: true });
  const primary =
    run.commitSha?.slice(0, 8) ?? run.buildId ?? run.pipelineExecutionId ?? run.id.slice(0, 8);
  const secondary = [run.projectName?.trim() ?? null, run.branch?.trim() ?? null]
    .filter((value): value is string => value !== null && value.length > 0)
    .join(' · ');
  const identity = secondary.length > 0 ? `${primary} · ${secondary}` : primary;

  return `${timestamp} · ${identity}`;
};

export const buildCoverageRunOptions = (runs: CoverageRunDto[]): FilterOption[] =>
  [...runs]
    .sort(
      (left, right) =>
        new Date(right.capturedAt ?? right.createdAt).getTime() -
        new Date(left.capturedAt ?? left.createdAt).getTime(),
    )
    .map((run) => ({
      label: buildCoverageRunOptionLabel(run),
      value: run.id,
    }));

export const buildCoverageDirectoryOptions = (files: CoverageFileDto[]): FilterOption[] =>
  [...new Set(files.map((file) => file.parentPath).filter((path): path is string => Boolean(path)))]
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((path) => ({ label: path, value: path }));

export const buildCoverageFileOptions = (files: CoverageFileDto[]): FilterOption[] =>
  [...new Set(files.map((file) => file.fileName).filter((name) => name.length > 0))]
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((name) => ({ label: name, value: name }));

export const buildCoverageTagOptions = (runs: CoverageRunDto[]): FilterOption[] =>
  Array.from(
    new Set(
      runs.flatMap((run) =>
        Array.isArray(run.tags)
          ? run.tags.filter((tag): tag is string => typeof tag === 'string' && tag.length > 0)
          : [],
      ),
    ),
  )
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((tag) => ({ label: tag, value: tag }));

export const buildCodeEntityTagOptions = (
  entities: Array<Pick<CodeDirectoryDto | CodeFileDto, 'tags'>>,
): FilterOption[] =>
  Array.from(
    new Set(
      entities.flatMap((entity) =>
        Array.isArray(entity.tags)
          ? entity.tags.filter((tag): tag is string => typeof tag === 'string' && tag.length > 0)
          : [],
      ),
    ),
  )
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((tag) => ({ label: tag, value: tag }));
